import React, { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import Layout from './components/Layout'
import { LocaleBootstrap } from './components/LocaleBootstrap'
import { useCrossDomainAuthSync } from './hooks/useCrossDomainAuthSync'
import { useGAPageView } from './hooks/useGAPageView'
import { useNaverWcsPageView } from './hooks/useNaverWcsPageView'
import { useOneTagPageView } from './hooks/useOneTagPageView'
import { useDesignScaleVar } from './hooks/useDesignScaleVar'
import { SUPPORTED_LOCALES, withLocale, type AppLocale } from './i18n/localeRouting'

const Home = lazy(() => import('./pages/Home'))
const BrandStory = lazy(() => import('./pages/BrandStory'))
const UseCases = lazy(() => import('./pages/UseCases'))
const ServiceFeatures = lazy(() => import('./pages/service/ServiceFeatures'))
const ServiceFeatureStepRedirect = lazy(() => import('./pages/service/ServiceFeatureStepRedirect'))
const ServicePricing = lazy(() => import('./pages/service/ServicePricing'))
const ServiceComparison = lazy(() => import('./pages/service/ServiceComparison'))
const ServiceFaq = lazy(() => import('./pages/service/ServiceFaq'))
const UseCasesParents = lazy(() => import('./pages/use-cases/UseCasesParents'))
const UseCasesDaycare = lazy(() => import('./pages/use-cases/UseCasesDaycare'))
const UseCasesEvents = lazy(() => import('./pages/use-cases/UseCasesEvents'))
const UseCasesSeniors = lazy(() => import('./pages/use-cases/UseCasesSeniors'))
const BlogInsights = lazy(() => import('./pages/blog/BlogInsights'))
const BlogUpdates = lazy(() => import('./pages/blog/BlogUpdates'))
const BlogPostDetail = lazy(() => import('./pages/blog/BlogPostDetail'))
const TermsOfService = lazy(() => import('./pages/TermsOfService'))
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'))
const YouthPolicy = lazy(() => import('./pages/YouthPolicy'))
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'))
const Contact = lazy(() => import('./pages/Contact'))
const NotFound = lazy(() => import('./pages/NotFound'))
const WebAppRedirect = lazy(() => import('./pages/WebAppRedirect'))

function RouteFallback() {
  return <div className="route-fallback" aria-busy="true" aria-live="polite" />
}

function localeRouteElements(locale: AppLocale): React.ReactElement[] {
  const p = (path: string) => withLocale(path, locale)
  const key = (id: string) => `${locale}-${id}`

  return [
    <Route key={key('home')} path={p('/')} element={<Layout><Home /></Layout>} />,
    <Route key={key('brand')} path={p('/brand-story')} element={<Layout><BrandStory /></Layout>} />,
    <Route
      key={key('features')}
      path={p('/service/features')}
      element={<Layout><ServiceFeatures /></Layout>}
    />,
    <Route
      key={key('feature-step')}
      path={p('/service/features/:stepSlug')}
      element={<Layout><ServiceFeatureStepRedirect /></Layout>}
    />,
    <Route
      key={key('pricing')}
      path={p('/service/pricing')}
      element={<Layout><ServicePricing /></Layout>}
    />,
    <Route
      key={key('comparison')}
      path={p('/service/comparison')}
      element={<Layout><ServiceComparison /></Layout>}
    />,
    <Route
      key={key('service-faq')}
      path={p('/service/faq')}
      element={<Navigate to={p('/blog/faq')} replace />}
    />,
    <Route key={key('use-cases')} path={p('/use-cases')} element={<Layout><UseCases /></Layout>} />,
    <Route
      key={key('parents')}
      path={p('/use-cases/parents')}
      element={<Layout><UseCasesParents /></Layout>}
    />,
    <Route
      key={key('daycare')}
      path={p('/use-cases/daycare')}
      element={<Layout><UseCasesDaycare /></Layout>}
    />,
    <Route
      key={key('events')}
      path={p('/use-cases/events')}
      element={<Layout><UseCasesEvents /></Layout>}
    />,
    <Route
      key={key('seniors')}
      path={p('/use-cases/seniors')}
      element={<Layout><UseCasesSeniors /></Layout>}
    />,
    <Route
      key={key('blog')}
      path={p('/blog')}
      element={<Navigate to={p('/blog/insights')} replace />}
    />,
    <Route
      key={key('insights')}
      path={p('/blog/insights')}
      element={<Layout><BlogInsights /></Layout>}
    />,
    <Route
      key={key('parenting')}
      path={p('/blog/parenting')}
      element={<Navigate to={p('/blog/insights')} replace />}
    />,
    <Route key={key('faq')} path={p('/blog/faq')} element={<Layout><ServiceFaq /></Layout>} />,
    <Route
      key={key('updates')}
      path={p('/blog/updates')}
      element={<Layout><BlogUpdates /></Layout>}
    />,
    <Route
      key={key('post')}
      path={p('/blog/post/:postId')}
      element={<Layout><BlogPostDetail /></Layout>}
    />,
    <Route key={key('gallery')} path={p('/gallery')} element={<Navigate to={p('/')} replace />} />,
    <Route key={key('terms')} path={p('/terms')} element={<Layout><TermsOfService /></Layout>} />,
    <Route key={key('privacy')} path={p('/privacy')} element={<Layout><PrivacyPolicy /></Layout>} />,
    <Route key={key('youth')} path={p('/youth')} element={<Layout><YouthPolicy /></Layout>} />,
    <Route
      key={key('cookie')}
      path={p('/cookie-policy')}
      element={<Layout><CookiePolicy /></Layout>}
    />,
    <Route key={key('contact')} path={p('/contact')} element={<Layout><Contact /></Layout>} />,
    <Route
      key={key('start')}
      path={p('/start')}
      element={<Layout><WebAppRedirect path="/book-creation" /></Layout>}
    />,
    <Route
      key={key('signup')}
      path={p('/signup')}
      element={<Layout><WebAppRedirect path="/login" /></Layout>}
    />,
    <Route key={key('examples')} path={p('/examples')} element={<Navigate to={p('/')} replace />} />,
  ]
}

function App() {
  useCrossDomainAuthSync()
  useGAPageView()
  useNaverWcsPageView()
  /* 원태그 삽입 */
  useOneTagPageView()
  useDesignScaleVar()
  return (
    <div className="App">
      <LocaleBootstrap />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {SUPPORTED_LOCALES.flatMap((locale) => localeRouteElements(locale))}
          <Route
            path="*"
            element={
              <Layout>
                <NotFound />
              </Layout>
            }
          />
        </Routes>
      </Suspense>
    </div>
  )
}

export default App
